import { useState } from 'react';
import { toast } from 'react-toastify';

import PropTypes from 'prop-types';

import { Button } from '@mui/material';

import UploadComponent from './UploadComponent';

import { maxFileSize } from '../Forms/utils';
import { normalizeImage } from '../Image/utils';

const getInitialImages = (input) => {
  if (input?.value) return input.value;
  return [];
};

const ImagesUploadContainer = (props) => {
  const {
    onUpload,
    uploadUrl,
    submitText,
    placeholder,
    disabled,
    isMulti,
    // two below are used on sr update
    imagesLimit,
    uploadedImagesCount,
    // exist inside form
    input = {},
    meta = {},
    isReadOnly,
  } = props;

  const [imageFiles, setImageFiles] = useState(() => getInitialImages(input));

  const updateFormValues = (images) => {
    // run if the component is rendered inside the form
    input?.onChange && input.onChange(images);
  };

  const addImage = async (args) => {
    const normalizedImages = await Promise.all(
      Array.from(args.target.files).map((file) => normalizeImage(file)),
    );

    let images = [...imageFiles, ...normalizedImages];

    // make sure the images limit is not exceeded
    if (imagesLimit && images.length + uploadedImagesCount > imagesLimit) {
      const imagesLeft = imagesLimit - uploadedImagesCount;
      images = images.slice(0, imagesLeft);
    }

    // if is not multi only the last selected image should remain
    if (!isMulti && images.length > 1) {
      images = [images[images.length - 1]];
    }

    setImageFiles(images);

    updateFormValues(images);
  };

  const removeImage = (index) => {
    const images = [...imageFiles];
    images.splice(index, 1);
    setImageFiles(images);

    updateFormValues(images);
  };

  // is used for sr update
  const onSubmit = async () => {
    const error = maxFileSize(imageFiles);

    if (error) return toast.error(error);

    const isUploaded = await onUpload(uploadUrl, imageFiles);
    isUploaded && setImageFiles([]);
    return null;
  };

  let canSelectMore = true;
  if (imagesLimit && uploadedImagesCount) {
    canSelectMore = imageFiles.length + uploadedImagesCount < imagesLimit;
  }

  const uploadProps = {
    input,
    meta,
    accept: 'image/*',
    isMulti,
    disabled: disabled || !canSelectMore,
    selectedFiles: imageFiles,
    addItem: addImage,
    removeItem: removeImage,
    placeholder,
    isReadOnly,
  };

  return (
    <div>
      <UploadComponent {...uploadProps} />
      {onUpload && imageFiles?.length > 0 && (
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={onSubmit}
          sx={{ mt: '1rem' }}
        >
          {submitText}
        </Button>
      )}
    </div>
  );
};

ImagesUploadContainer.propTypes = {
  onUpload: PropTypes.func,
  uploadUrl: PropTypes.string,
  placeholder: PropTypes.string,
  submitText: PropTypes.string,
  disabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  // two below are used on sr update
  uploadedImagesCount: PropTypes.number,
  imagesLimit: PropTypes.number,
  // exist inside form
  input: PropTypes.instanceOf(Object),
  meta: PropTypes.instanceOf(Object),
  isReadOnly: PropTypes.bool,
};

export default ImagesUploadContainer;
